import { h } from 'preact';
import { useEffect, useMemo, useState } from 'preact/hooks';

import { useAuth } from '../../../../auth/hooks/use-auth.hook';
import { LegacyAuthModalState } from '../../../../auth/models/auth.model';
import { authenticationFlowService } from '../../../../auth/services/authentication-flow.service';
import { SpinnerComponent } from '../../../../common/components/spinner/spinner.component';
import { IconLock } from '../../../../common/icons/icon-lock';
import { IconSignalStream } from '../../../../common/icons/icon-signal-stream';
import { useTranslation } from '../../../../i18n';
import type { Membership, Tier } from '../../../../membership/models/membership.model';
import { NeedAccessButton } from '../../../../need-access/components/need-access-button/need-access-button.component';
import type {
  NeedAccessReq,
  NeedAccessResponse,
} from '../../../../need-access/need-access.interface';
import {
  AccessResponse,
  NeedAccessContentType,
} from '../../../../need-access/need-access.interface';
import { NeedAccessService } from '../../../../need-access/need-access.service';
import { useAppDispatch, useAppSelector } from '../../../../store/hooks';
import { buttonize } from '../../../../utils/a11y.utils';
import type { Video } from '../../../models/video.interface';
import { selectNeedAccess, selectNeedAccessLoaded } from '../../../store/content.selectors';
import { loadNeedAccess } from '../../../store/content.thunk';

interface VideoOverlayProps {
  video: Video;
  contentId: string;
  showTitle: boolean;
  showArtist: boolean;
  showDescription: boolean;
  showCountdown: boolean;
  freeOnlyButtonText: string;
  freePaidButtonText: string;
  paidOnlyButtonText: string;
  buttonColor: string;
  buttonTextColor: string;
  textColor: string;
  borderRadius?: string;
  borderWidth?: string;
  borderColor?: string;
  /** A membership join is in flight — show a centered spinner instead of the
   *  access lock so mid-join fans don't see "You do not have access" (SNGL-6967). */
  joining?: boolean;
}

export function VideoOverlay({
  video,
  contentId,
  showTitle,
  showArtist,
  showDescription,
  showCountdown,
  freeOnlyButtonText,
  freePaidButtonText,
  paidOnlyButtonText,
  buttonColor,
  buttonTextColor,
  textColor,
  borderRadius,
  borderWidth,
  borderColor,
  joining,
}: VideoOverlayProps) {
  const { t, locale } = useTranslation();

  const [, setTick] = useState(0);

  const dispatch = useAppDispatch();
  const loaded: boolean = useAppSelector(selectNeedAccessLoaded);
  const needAccessService = new NeedAccessService();
  const needAccessResponse: NeedAccessResponse = useAppSelector(selectNeedAccess);
  const { accessResponse } = needAccessResponse || {};
  const { openAuthModal: authModalOpen } = useAuth();
  let showContReading: boolean;

  const eventDate = needAccessResponse?.content?.eventDate ?? video.eventDate;

  useEffect(() => {
    if (!showCountdown || !eventDate) return;
    const interval = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(interval);
  }, [showCountdown, eventDate]);

  const countdownDisplay = (() => {
    if (!showCountdown || !eventDate) return null;
    const distance = new Date(eventDate as string).getTime() - Date.now();
    if (distance <= 0) return null;
    const days = Math.floor(distance / (1000 * 60 * 60 * 24));
    const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((distance % (1000 * 60)) / 1000);
    return `${days}d ${hours}h ${minutes}m ${seconds}s`;
  })();

  const openAuthModal = async (state: LegacyAuthModalState) => {
    await authenticationFlowService.initiateAuthentication({ state });
  };

  const allMembershipsHaveOnlyFreeTiers = (memberships: Membership[]): boolean => {
    return memberships.every(
      (membership: Membership) =>
        membership.tiers.length > 0 && membership.tiers.every((tier: Tier) => tier.freeTier)
    );
  };

  const accessData = useMemo(() => {
    const products = needAccessResponse?.keyProducts
      ?.map((p) => p.product)
      .filter((p) => !p.error && p.type !== 'Membership' && p.type !== 'Fan Subscription');

    const hasProductAccessKeys = !!products?.length || !!needAccessResponse?.keyListings?.length;
    const hasMembershipAccessKeys = !!needAccessResponse?.keyMemberships?.length;

    let message: string | undefined;

    if (hasMembershipAccessKeys) {
      const onlyFreeTiers = allMembershipsHaveOnlyFreeTiers(needAccessResponse?.keyMemberships);
      message = onlyFreeTiers
        ? freeOnlyButtonText || t('access.joinToWatch')
        : freePaidButtonText || t('access.joinOrPurchaseAccess');
    } else if (hasProductAccessKeys) {
      message = paidOnlyButtonText || t('access.purchaseAccess');
    }

    return { message, hasProductAccessKeys, hasMembershipAccessKeys };
  }, [needAccessResponse, freeOnlyButtonText, freePaidButtonText, paidOnlyButtonText, locale]);

  const { message, hasProductAccessKeys, hasMembershipAccessKeys } = accessData;
  const needAccessMessage = message ?? t('access.joinToWatch');

  useEffect(() => {
    dispatch(loadNeedAccess({ contentId, contentType: NeedAccessContentType.VIDEO }));
  }, [dispatch]);

  const noAccessDesc = (): string => {
    const tempEl = document.createElement('div');
    tempEl.innerHTML = video.description;

    const fullText = (tempEl.textContent || tempEl.innerText || '').trim();
    const childNodes = Array.from(tempEl.childNodes);

    let charCount = 0;
    let output = '';
    let done = false;

    for (const node of childNodes) {
      if (done) {
        break;
      }

      let innerText = '';

      if (node.nodeType === Node.TEXT_NODE) {
        innerText = node.textContent?.trim() || '';
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        const el = node as HTMLElement;
        innerText = el.textContent?.trim() || '';
        if (innerText && !/[.!?]$/.test(innerText)) {
          innerText += '.';
        }
      }

      if (!innerText) {
        continue;
      }

      if (charCount + innerText.length > 300) {
        output += innerText.slice(0, 300 - charCount).trimEnd();
        done = true;
        break;
      }

      output += innerText + ' ';
      charCount += innerText.length;
    }

    output = output.trim();

    showContReading = output.length < fullText.length;

    return showContReading
      ? output + `...<u style="cursor: pointer">${t('perks.continueReading')}</u>`
      : output;
  };

  const processNeedAccessRequest = async () => {
    if (showContReading) {
      const req: NeedAccessReq = {
        contentId: video.id,
        contentType: NeedAccessContentType.VIDEO,
      };
      await needAccessService.processNeedAccessRequest({
        request: req,
        modalMaxWidth: 1200,
        modalBorderRadius: borderRadius,
        modalBorderWidth: borderWidth,
        modalBorderColor: borderColor,
        dispatch,
        openAuthModal: authModalOpen,
      });
    }
  };

  return (
    <div class="video-overlay" style={{ backgroundImage: `url(${video.thumbnailUrl})` }}>
      {joining && (
        <div class="no-access">
          <SpinnerComponent />
        </div>
      )}
      {!joining && showCountdown && countdownDisplay && (
        <div class="video-countdown">
          <div class="countdown-icon">
            <IconSignalStream width={28} height={28} fill="#fff" />
          </div>
          <div class="countdown-clock">
            <div class="countdown-showtime">{t('video.startsIn')}</div>
            <div>{countdownDisplay}</div>
          </div>
        </div>
      )}
      {!joining && loaded && (
        <div class="no-access">
          <IconLock width={40} height={40} fill={textColor || '#fff'} />
          {/*------ REQUEST LOGIN ------*/}
          {accessResponse === AccessResponse.REQUEST_LOGIN && (
            <>
              {showTitle && <h2 class="h2 single-h2">{video.name}</h2>}
              {showArtist && <div class="single-video-artist">{video.artistName}</div>}
              {showDescription && !!video.description && (
                <div class="single-video-description-wrapper">
                  <div
                    class="single-video-description"
                    dangerouslySetInnerHTML={{ __html: noAccessDesc() }}
                    {...buttonize(processNeedAccessRequest)}
                  />
                </div>
              )}
              {(hasProductAccessKeys || hasMembershipAccessKeys) && (
                <NeedAccessButton
                  message={needAccessMessage}
                  contentType={NeedAccessContentType.VIDEO}
                  contentId={video.id}
                  showLock={false}
                  modalMaxWidth={1200}
                  modalBorderRadius={borderRadius}
                  modalBorderWidth={borderWidth}
                  modalBorderColor={borderColor}
                  backgroundColor={buttonColor}
                  textColor={buttonTextColor}
                />
              )}

              {hasProductAccessKeys && !hasMembershipAccessKeys && (
                <p
                  class="single-login-link"
                  {...buttonize(() => openAuthModal(LegacyAuthModalState.LOGIN))}
                >
                  <span>{t('access.alreadyPurchased')}&nbsp;</span>
                  <span class="text-underline">{t('auth.logIn')}</span>
                </p>
              )}
              {accessResponse === AccessResponse.REQUEST_LOGIN &&
                (!hasProductAccessKeys || hasMembershipAccessKeys) && (
                  <p
                    class="single-login-link"
                    {...buttonize(() => openAuthModal(LegacyAuthModalState.LOGIN))}
                  >
                    <span class="text-underline">{t('auth.logIn')}</span>
                  </p>
                )}
            </>
          )}
          {/*------ ACCESS DENIED -------*/}
          {accessResponse === AccessResponse.ACCESS_DENIED && (
            <>
              <h2 class="h2 single-h2">{t('access.noAccess')}</h2>
              <p class="single-access-denied-text">{t('access.logInWithEmail')}</p>
              <p class="single-access-denied-text-or">{t('access.or')}</p>
              {(hasProductAccessKeys || hasMembershipAccessKeys) && (
                <NeedAccessButton
                  message={needAccessMessage}
                  contentType={NeedAccessContentType.VIDEO}
                  contentId={video.id}
                  showLock={false}
                  modalMaxWidth={1200}
                  modalBorderRadius={borderRadius}
                  modalBorderWidth={borderWidth}
                  modalBorderColor={borderColor}
                  backgroundColor={buttonColor}
                  textColor={buttonTextColor}
                />
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
